import { Button } from '@/components/ui/button';
import { Pencil, Tags, Link2, Trash2, Save, X } from 'lucide-react';

interface EntityRowActionsProps {
  isEditing: boolean;
  onEdit?: () => void;
  onTags?: () => void;
  onLink?: () => void;
  onDelete?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  canEdit?: boolean;
  canLink?: boolean;
  isSaving?: boolean;
  stopPropagation?: boolean;
}

/**
 * Shared row action buttons for entity tables
 * Shows Edit/Tags/Link/Delete in view mode
 * Shows Save/Cancel in edit mode
 */
export function EntityRowActions({
  isEditing,
  onEdit,
  onTags,
  onLink,
  onDelete,
  onSave,
  onCancel,
  canEdit = true,
  canLink = true,
  isSaving = false,
  stopPropagation = true
}: EntityRowActionsProps) {
  const handleClick = (e: React.MouseEvent, callback?: () => void) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    callback?.();
  };

  if (isEditing) {
    return (
      <div className="flex items-center justify-end gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => handleClick(e, onSave)}
          disabled={isSaving}
          className="h-8 w-8"
          title="Save changes"
        >
          <Save className="h-4 w-4 text-green-600 hover:text-green-700" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => handleClick(e, onCancel)}
          className="h-8 w-8"
          title="Cancel editing"
        >
          <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {canEdit && onEdit && (
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => handleClick(e, onEdit)}
          className="h-8 w-8"
          title="Edit metadata"
        >
          <Pencil className="h-4 w-4 text-muted-foreground hover:text-primary" />
        </Button>
      )}
      {canEdit && onTags && (
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => handleClick(e, onTags)}
          className="h-8 w-8"
          title="Edit tags"
        >
          <Tags className="h-4 w-4 text-muted-foreground hover:text-primary" />
        </Button>
      )}
      {canLink && onLink && (
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => handleClick(e, onLink)}
          className="h-8 w-8"
          title="Link to other items"
        >
          <Link2 className="h-4 w-4 text-muted-foreground hover:text-primary" />
        </Button>
      )}
      {canEdit && onDelete && (
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => handleClick(e, onDelete)}
          className="h-8 w-8"
          title="Delete"
        >
          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
        </Button>
      )}
    </div>
  );
}
